import React, { createContext, useContext, useEffect, useState } from 'react'

import { useLocation } from 'react-router-dom'

import { PartType } from '@models/editor/Part'
import { useAudioContext } from '@providers/AudioContextProvider'
import TitleService from '@services/TitleService'
import nfsyn from '@utils/nf-webaudio/nf-webaudio'
import { SequencerOptions, SequencerType } from '@utils/nf-webaudio/sequencer'
import { Sequence } from '@utils/nf-webaudio/sequencer.types'

export type AudioPreviewContextType = {
  productUUID: string | null
  preview: Sequence | null
  startPreview: (url: string, productUUID: string) => void
  stopPreview: () => void
  isPlaying: (productUUID: string) => boolean
}

export const AudioPreviewContext = createContext<AudioPreviewContextType>({
  productUUID: '',
  preview: null,
  startPreview: () => null,
  isPlaying: () => false,
  stopPreview: () => null,
})

const AudioPreviewProvider: React.FC = ({ children }) => {
  const { audioContext } = useAudioContext()
  const location = useLocation()

  const [productUUID, setProductUUID] = useState<string | null>(null)
  const [preview, setPreview] = useState<Sequence | null>(null)
  const [sequencer, setSequencer] = useState<SequencerType | null>(null)

  const stopPreview = () => {
    if (sequencer) {
      sequencer.stop()
    }
    setProductUUID(null)
    setPreview(null)
  }

  const contextValue: AudioPreviewContextType = {
    productUUID,
    preview,
    startPreview: async (url, uuid) => {
      if (sequencer) {
        sequencer.stop()
      }

      setProductUUID(uuid)

      const scorePreview = await TitleService.getProductPreview(url)
      if (!scorePreview) return

      setPreview(scorePreview)

      if (!audioContext) {
        console.error('Error finding audio preview')
        return
      }

      const seqOpts: SequencerOptions = { loopStart: -1, loopEnd: -1 }
      const currSequencer: SequencerType = new nfsyn.Sequencer(
        audioContext,
        audioContext.manifest,
        scorePreview,
        seqOpts
      )

      setSequencer(currSequencer)

      const loadSynthesizers: Promise<void>[] = scorePreview.parts.map(
        (part: PartType) =>
          new Promise((resolve) => {
            // Creates a sampler for each instrument
            audioContext.setSynthesizerForPart(part, resolve)
          })
      )

      // Once synthesizers have all their samples loaded up - play some music!
      Promise.all(loadSynthesizers).then(() => {
        currSequencer.startPlaying()
      })
    },
    stopPreview,
    isPlaying: (uuid) => productUUID === uuid,
  }

  useEffect(() => {
    return () => sequencer?.stop()
  })

  // Stop preview when page route changes and component unmounts
  useEffect(() => {
    stopPreview()
    return () => {
      stopPreview()
    }
  }, [location])

  return (
    <AudioPreviewContext.Provider value={contextValue}>{children}</AudioPreviewContext.Provider>
  )
}

export default AudioPreviewProvider

export const useAudioPreview = (): AudioPreviewContextType => useContext(AudioPreviewContext)
